/*
 * Decides the page language before anything renders. Imported first in
 * index.js, so i18n and every component read the same value.
 *
 * The URL wins: /Avocats/… is French, /محامي/… Arabic, /en is English. Only
 * pages whose URL carries no language (login, dashboards…) fall back to the
 * saved choice, then to the mobile app's French, then to Arabic.
 */
import urls from "@seo/urls";
import { setPageLang } from "./links";

function readSaved() {
  try {
    return localStorage.getItem("lang");
  } catch (e) {
    return null;
  }
}

function detectLang() {
  const fromUrl = urls.parsePath(window.location.pathname, window.location.search).lang;
  if (fromUrl) return fromUrl;
  const saved = readSaved();
  if (urls.LANGS.includes(saved)) return saved;
  if (navigator.userAgent === "AvoconsulteApp") return "fr";
  return "ar";
}

/** "rtl" or "ltr": the direction, and so which of the two template stylesheets, a language needs. */
export function direction(language) {
  return language === "ar" ? "rtl" : "ltr";
}

/**
 * Decide the language for the current URL and apply it everywhere it is
 * read: currentLang() (in memory, whatever localStorage later says),
 * localStorage (for the pages whose URL carries no language), and
 * <html lang dir>. Returns the language.
 */
export function bootLanguage() {
  const decided = detectLang();
  setPageLang(decided);
  try {
    localStorage.setItem("lang", decided);
  } catch (e) {
    // Private mode: the language still comes from the URL on every page.
  }
  document.documentElement.lang = decided;
  document.documentElement.dir = direction(decided);
  return decided;
}

export const lang = bootLanguage();

/**
 * The template ships one stylesheet per direction. Start fetching the right
 * one now; index.js renders once it has applied so React never paints
 * unstyled. Named chunks (style-ltr / style-rtl) so their files are easy to
 * find in dist/assets.
 */
export const stylesReady = (direction(lang) === "rtl"
  ? import(/* webpackChunkName: "style-rtl" */ "../../assets/style-rtl.scss")
  : import(/* webpackChunkName: "style-ltr" */ "../../assets/style.scss")
).catch(() => null);

/** How long the first render waits for the site font at most. */
export const FONT_WAIT_MS = 1500;

/**
 * The site font (Tajawal, @import-ed by assets/avc/theme.css) in this
 * language's script. index.js waits for it, briefly, before the first
 * render: text laid out in the fallback font and laid out again when
 * Tajawal arrived moved the Arabic home hero by a fifth of a phone screen
 * (layout shift). Never rejects; resolves after `timeout` at the latest.
 */
export function fontsReady(language, timeout = FONT_WAIT_MS) {
  const fonts = document.fonts;
  if (!fonts || typeof fonts.load !== "function") return Promise.resolve();
  const sample = language === "ar" ? "محامي في تونس" : "Avocat en Tunisie";
  const loaded = Promise.all(["400", "500", "700"].map((weight) => fonts.load(`${weight} 16px Tajawal`, sample))).catch(
    () => null
  );
  return Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, timeout))]);
}
